import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface LoginPayload { email: string; password: string; }

export interface AdminData {
  _id: string; fullName: string; email: string;
  adminType: string; isActive: boolean; access: Record<string, unknown>;
}

export interface LoginResponse {
  success: boolean; code: number; message: string;
  data: { token: string; admin: AdminData; };
}

export interface RegenerateTokenResponse {
  success: boolean; code: number; message?: string;
  data: { token: string };
}

// How long before the token's `exp` claim to request a new one.
const REFRESH_BEFORE_EXPIRY_MS = 20 * 1000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http   = inject(HttpClient);
  private router = inject(Router);

  isLoggedIn = signal<boolean>(!!localStorage.getItem('token'));
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (this.getToken()) this.scheduleAutoRefresh();
  }

  login(payload: LoginPayload): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${environment.apiUrl}/api/admin/auth/login`, payload
    ).pipe(
      switchMap(res => {
        if (!res.success || !res.data?.token) {
          return throwError(() => new Error(res.message || 'Invalid credentials.'));
        }
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('admin', JSON.stringify(res.data.admin));
        this.isLoggedIn.set(true);
        this.scheduleAutoRefresh();
        return of(res);
      })
    );
  }

  logout(): void {
    this.clearRefreshTimer();
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    this.isLoggedIn.set(false);
    this.router.navigate(['/auth/login']);
  }

  getToken(): string | null { return localStorage.getItem('token'); }

  getAdmin(): AdminData | null {
    const raw = localStorage.getItem('admin');
    return raw ? JSON.parse(raw) : null;
  }

  regenerateToken(): Observable<RegenerateTokenResponse> {
    return this.http.post<RegenerateTokenResponse>(
      `${environment.apiUrl}/api/admin/auth/regenerate-token`, {}
    ).pipe(
      switchMap(res => {
        if (!res.success || !res.data?.token) {
          return throwError(() => new Error(res.message || 'Could not refresh session.'));
        }
        localStorage.setItem('token', res.data.token);
        this.scheduleAutoRefresh();
        return of(res);
      })
    );
  }

  // Reads the `exp` claim (seconds since epoch) out of the JWT payload without verifying
  // the signature — this only decides *when* to proactively refresh; the server is still
  // the one that validates the token on every request.
  private getTokenExpiryMs(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const { exp } = JSON.parse(json);
      return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
      return null;
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private scheduleAutoRefresh(): void {
    this.clearRefreshTimer();

    const token = this.getToken();
    if (!token) return;

    const expiryMs = this.getTokenExpiryMs(token);
    if (!expiryMs) return;

    const delay = expiryMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS;
    if (delay <= 0) {
      this.regenerateToken().subscribe({ error: () => this.logout() });
      return;
    }

    this.refreshTimer = setTimeout(() => {
      this.regenerateToken().subscribe({ error: () => this.logout() });
    }, delay);
  }
}
